/**
 * useRecent — tracks the 5 most-recently-visited entity pages. Hooked from a
 * shared route-watcher that lives in AppShell so we don't need a useRecent in
 * every detail page.
 */
import { useCallback, useEffect, useState } from 'react';
import type { FavoriteKind } from './useFavorites';

export interface RecentItem {
  kind: FavoriteKind;
  id: string;
  title: string;
  visitedAt: string;
}

const STORAGE_KEY = 'pipebase.recent.v1';
const MAX_RECENT = 5;

function read(): RecentItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (r) => r && typeof r.kind === 'string' && typeof r.id === 'string',
    );
  } catch {
    return [];
  }
}

function write(list: RecentItem[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    window.dispatchEvent(new CustomEvent('pipebase:recent:changed'));
  } catch {
    /* ignore */
  }
}

export function useRecent() {
  const [items, setItems] = useState<RecentItem[]>(() => read());

  useEffect(() => {
    const onChange = () => setItems(read());
    window.addEventListener('storage', onChange);
    window.addEventListener('pipebase:recent:changed', onChange as EventListener);
    return () => {
      window.removeEventListener('storage', onChange);
      window.removeEventListener('pipebase:recent:changed', onChange as EventListener);
    };
  }, []);

  const record = useCallback((entry: Omit<RecentItem, 'visitedAt'>) => {
    const list = read().filter(
      (r) => !(r.kind === entry.kind && r.id === entry.id),
    );
    const next: RecentItem[] = [
      { ...entry, visitedAt: new Date().toISOString() },
      ...list,
    ].slice(0, MAX_RECENT);
    write(next);
    setItems(next);
  }, []);

  return { items, record };
}

export const __recentInternals = { STORAGE_KEY, MAX_RECENT };
